import { computed, observable } from 'mobx'
import { getAddress, isAddress, isAddressEqual, parseUnits } from 'viem'
import { UNIT } from '@/constants/wallet.js'
import {
  compactNumberFormatter,
  durationFromDates,
  formatMweiAsUsd,
  formatMweiAsUsdCompact,
  numberFormatter,
  truncateAddress,
} from '@/utils/format.js'
import { differenceInSeconds, isBefore } from 'date-fns'
import { POST_TYPES } from '@/constants/post.js'

export default class ReportModel {
  rootStore = null

  @observable accessor type = ''
  @observable accessor createdAt = ''
  @observable accessor startedAt = ''
  @observable accessor purchasedAt = ''
  @observable accessor endedAt = ''
  @observable accessor senderAddress = ''
  @observable accessor receiverAddress = ''
  @observable accessor transactionHash = ''
  @observable accessor amount = 0n
  @observable accessor fee = 0n
  @observable accessor incomeGross = 0n
  @observable accessor participantCount = 0
  @observable accessor transactionType = ''
  @observable accessor handle = ''
  @observable accessor postOwnerNickname = ''
  @observable accessor title = ''
  @observable accessor postId = ''

  constructor(rootStore, type, reportData) {
    this.rootStore = rootStore
    this.type = type
    this.#update(reportData)
  }

  get auth() {
    return this.rootStore.authStore
  }

  @computed
  get isPurchase() {
    return this.type === 'purchase'
  }

  @computed
  get isRevenue() {
    return this.type === 'revenue'
  }

  @computed
  get isTransaction() {
    return this.type === 'transaction'
  }

  @computed
  get isTransactionSender() {
    return isAddressEqual(this.senderAddress, this.auth.user.address)
  }

  @computed
  get isTransactionReceiver() {
    return isAddressEqual(this.receiverAddress, this.auth.user.address)
  }

  @computed
  get isPolygonTransaction() {
    return isBefore(this.createdAt, '2025-03-20T13:50:00.000Z')
  }

  @computed
  get hasParticipants() {
    return this.participantCount > 0
  }

  @computed
  get participantCountFormatted() {
    return numberFormatter.format(this.participantCount)
  }

  @computed
  get participantCountFormattedCompact() {
    return compactNumberFormatter.format(this.participantCount)
  }

  @computed
  get isFree() {
    return this.amount === 0n
  }

  @computed
  get amountFormatted() {
    return formatMweiAsUsd(this.amount)
  }

  @computed
  get amountFormattedCompact() {
    return formatMweiAsUsdCompact(this.amount)
  }

  @computed
  get feeFormatted() {
    return formatMweiAsUsd(this.fee)
  }

  @computed
  get feeFormattedCompact() {
    return formatMweiAsUsdCompact(this.fee)
  }

  @computed
  get incomeGrossFormatted() {
    return formatMweiAsUsd(this.incomeGross)
  }

  @computed
  get incomeGrosSFormattedCompact() {
    return formatMweiAsUsdCompact(this.incomeGross)
  }

  @computed
  get incomeNet() {
    return this.incomeGross - this.fee
  }

  @computed
  get incomeNetFormatted() {
    return formatMweiAsUsd(this.incomeNet)
  }

  @computed
  get incomeNetFormattedCompact() {
    return formatMweiAsUsdCompact(this.incomeNet)
  }

  @computed
  get createdAtDateFormatted() {
    return new Date(this.createdAt).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  @computed
  get createdAtTimeFormatted() {
    return new Date(this.createdAt).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  @computed
  get purchasedAtDateFormatted() {
    return new Date(this.purchasedAt).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  @computed
  get purchasedAtTimeFormatted() {
    return new Date(this.purchasedAt).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  @computed
  get hasStartedAndEnded() {
    return this.startedAt && this.endedAt
  }

  @computed
  get startedAtDateFormatted() {
    return this.startedAt
      ? new Date(this.startedAt).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : ''
  }

  @computed
  get startedAtTimeFormatted() {
    return this.startedAt
      ? new Date(this.startedAt).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : ''
  }

  @computed
  get endedAtDateFormatted() {
    return this.endedAt
      ? new Date(this.endedAt).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : ''
  }

  @computed
  get endedAtTimeFormatted() {
    return this.endedAt
      ? new Date(this.endedAt).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : ''
  }

  @computed
  get duration() {
    return this.hasStartedAndEnded ? differenceInSeconds(this.endedAt, this.startedAt) : 0
  }

  @computed
  get durationFormatted() {
    return this.hasStartedAndEnded ? durationFromDates(this.startedAt, this.endedAt) : ''
  }

  #update = reportData => {
    this.createdAt = reportData.createtime ?? reportData.date_time ?? this.createdAt
    this.startedAt = reportData.joined_at ?? this.startedAt
    this.endedAt = reportData.left_at ?? this.endedAt
    this.purchasedAt = reportData.date_time ?? reportData.joined_at ?? this.purchasedAt
    this.participantCount = Number(reportData.nb_participants ?? this.participantCount)
    this.title = reportData.room_name ?? this.title
    this.postId = reportData.meet_id ?? this.postId
    this.transactionHash = reportData.transactionHash ?? this.transactionHash

    if (reportData.price) this.amount = parseUnits(reportData.price.toString(), UNIT.MWEI)
    else if (reportData.cost) this.amount = parseUnits(reportData.cost.toString(), UNIT.MWEI)

    if (reportData.fee) this.fee = parseUnits(reportData.fee.toString(), UNIT.MWEI)
    if (reportData.totalReceived)
      this.incomeGross = parseUnits(reportData.totalReceived.toString(), UNIT.MWEI)

    if (isAddress(reportData.sender)) this.senderAddress = getAddress(reportData.sender)
    if (isAddress(reportData.receiver)) this.receiverAddress = getAddress(reportData.receiver)

    // FIXME: BE should send proper user info along with the report
    this.postOwnerNickname = reportData.nickname ?? this.postOwnerNickname
    if (isAddress(this.postOwnerNickname))
      this.postOwnerNickname = truncateAddress(getAddress(this.postOwnerNickname))

    // FIXME: this is bad, like on post model
    this.transactionType = this.#getTransactionType(reportData.device ?? this.transactionType)
    // NOT USED: type, difference, id, latest, method, duration, handle
  }

  #getTransactionType = device => {
    switch (device.trim().toLowerCase()) {
      case 'webcam':
        return POST_TYPES.MEETING

      case 'obs':
        return POST_TYPES.LIVESTREAM_OBS

      case 'bundle':
        return POST_TYPES.IMAGE

      case 'upload':
        return POST_TYPES.VIDEO

      case 'messaging':
        return this.incomeGross > 0n && this.fee > 0n ? 'tip' : 'payment'

      // technical debt (:
      case '':
        return null

      default:
        console.error(`[Report] Could not identify transaction type: ${device}`)

        return null
    }
  }
}
